// Per-Type Catalog , field relevance (which item kinds + profile sub-fields make
// sense for the active branch's business type + operating modes).
// docs/expansion-roadmap-2026/38-per-type-catalog-branch-sharing-and-inventory.md (Part 1)
// docs/expansion-roadmap-2026/34-business-type-profiles-standard-catering-bar.md
//
// DESIGN LAW (doc 33): this is PRESENTATION ONLY. It never authorizes or forbids ,
// it decides what the editor *leads with* vs. tucks behind "more", so a plain
// restaurant isn't shown catering deposit fields or a bar's age-gate. Nothing here
// locks a choice: every kind stays reachable (mixed-model), and any field that
// already holds a value is always shown so editing a legacy/mixed item never hides
// data. "Not relevant" means "hidden until asked for", not "disabled".
//
// Client-safe: no server-only imports. Operating modes are passed in as plain
// strings (the client reads them via useBranchOperatingModes).

import {
  DEFAULT_BUSINESS_TYPE,
  getSubType,
  isBusinessTypeId,
  type BusinessTypeId,
} from '@/lib/business-types/registry';

import { ITEM_KIND_IDS, type ItemKind } from './types';

// The operating modes this layer keys on. A superset-safe string list , callers
// pass whatever the branch has active; we only look for the ones that matter here.
export type ActiveMode = string;

function hasMode(activeModes: readonly ActiveMode[] | undefined, mode: string): boolean {
  return Boolean(activeModes?.includes(mode));
}

// ─── Which item kinds a menu should lead with ────────────────────────────────
// `primary` kinds are shown as first-class choices; `extra` kinds stay one tap
// behind a "More types" disclosure (still fully selectable , mixed-model).

export interface KindRelevance {
  primary: ItemKind[];
  extra: ItemKind[];
}

/**
 * Which item kinds are available for a given business type + operating modes.
 * Returns `allowed` (kinds the merchant can use) split into `primary` (shown
 * prominently) and `extra` (behind "More types"). Kinds outside `allowed` are
 * not shown at all , a caterer doesn't need to see "combo", a fast-food shop
 * doesn't need "package".
 *
 * Every kind stays reachable via the API (mixed-model), but the UI only
 * surfaces what makes sense for the business type.
 */
export function resolveKindRelevance(ctx: {
  typeId: string | null | undefined;
  /**
   * The branch's `specific_type`. A sub-type may widen the catalog it leads with
   * (`SubTypeProfile.primaryItemKinds`) , this parameter is what carries that
   * through. Before it existed, only the broad type was consulted, so a Fast Food
   * branch was judged as plain `standard` and could not author a combo meal.
   */
  specificType?: string | null;
  activeModes?: readonly ActiveMode[];
}): KindRelevance & { allowed: ItemKind[] } {
  // Only `standard` and `catering` are active business types. A legacy branch
  // still holding an off type id (bar, buffet, ghost_kitchen, hotel) degrades to
  // `standard` here, exactly like `getBusinessType()` in the registry.
  const type: BusinessTypeId = isBusinessTypeId(ctx.typeId) ? ctx.typeId : DEFAULT_BUSINESS_TYPE;
  const allowed = new Set<ItemKind>();
  const primary = new Set<ItemKind>();

  // A dish is the universal food line , always available and primary.
  allowed.add('dish');
  primary.add('dish');

  switch (type) {
    case 'catering':
      // Caterers: dish (simple items), package (the core product), service (add-ons).
      // No drink (not their model), no combo (fast food concept).
      allowed.add('package');
      allowed.add('service');
      primary.add('package');
      primary.add('service');
      // If bar service is also on, allow drinks.
      if (hasMode(ctx.activeModes, 'bar_service')) {
        allowed.add('drink');
        primary.add('drink');
      }
      break;

    case 'standard':
      // Standard (restaurant/cafe): dish + drink. No package, no service, no combo.
      allowed.add('drink');
      primary.add('drink');
      // If scheduled pre-orders are on, allow packages + services.
      if (hasMode(ctx.activeModes, 'scheduled_pre_order')) {
        allowed.add('package');
        allowed.add('service');
        primary.add('package');
        primary.add('service');
      }
      if (hasMode(ctx.activeModes, 'bar_service')) {
        allowed.add('drink');
        primary.add('drink');
      }
      if (hasMode(ctx.activeModes, 'counter_pickup')) {
        allowed.add('combo');
      }
      break;
  }

  // Sub-type layer, applied last and additively (doc 48 §2: a sub-type modifies
  // its parent, it never replaces it). Additive only , widening the catalog can
  // never hide a kind a mixed menu is already using.
  for (const kind of getSubType(ctx.typeId, ctx.specificType)?.primaryItemKinds ?? []) {
    allowed.add(kind);
    primary.add(kind);
  }

  const allowedArr = ITEM_KIND_IDS.filter((k) => allowed.has(k));
  const primaryArr = allowedArr.filter((k) => primary.has(k));
  const extraArr = allowedArr.filter((k) => !primary.has(k));

  return { primary: primaryArr, extra: extraArr, allowed: allowedArr };
}

// ─── Profile sub-field relevance ─────────────────────────────────────────────
// Some structured fields only mean something when a supporting mode is on. A
// deposit % is inert without scheduled pre-orders; an age gate is inert without
// bar service. Hide the noise, but honour a value that's already set.

export type ProfileField =
  // package
  | 'deposit_pct'
  | 'min_headcount'
  | 'lead_time_days'
  // drink
  | 'age_restricted'
  | 'happy_hour_price_delta';

export function isProfileFieldRelevant(
  field: ProfileField,
  ctx: { typeId?: string | null; activeModes?: readonly ActiveMode[] },
): boolean {
  switch (field) {
    // Deposits require the scheduled-pre-order/deposit workflow to collect against.
    case 'deposit_pct':
      return ctx.typeId === 'catering' || hasMode(ctx.activeModes, 'scheduled_pre_order');
    // Headcount + lead time describe the package itself , relevant wherever
    // packages are (catering or scheduled pre-orders on).
    case 'min_headcount':
    case 'lead_time_days':
      return ctx.typeId === 'catering' || hasMode(ctx.activeModes, 'scheduled_pre_order');
    // The age gate only fires when bar service (age verification) is active.
    case 'age_restricted':
      return hasMode(ctx.activeModes, 'bar_service');
    // Happy-hour pricing is a bar concept (and its engine is bar-scoped).
    case 'happy_hour_price_delta':
      return hasMode(ctx.activeModes, 'bar_service');
    default:
      return true;
  }
}

// ─── Advanced (allergens / dietary / nutrition) relevance ────────────────────
// These are food-facing. A dish or a catering package needs them; a bar drink or
// a service line (staff hours, corkage) almost never does , hide by default,
// still reveal if the item already carries any of that data.

export function isNutritionSectionRelevant(kind: ItemKind): boolean {
  return kind === 'dish' || kind === 'package';
}
